import { PautaParseError } from './PautaErrors'
import type { PautaProduct, PautaRawProductPayload } from './PautaTypes'

/**
 * PautaParser — prepares structured interpretation of provider payloads.
 * No HTML/DOM handling in Sprint 3.2; accepts JSON-like / simulated objects.
 */
export class PautaParser {
  parseProduct(raw: unknown): PautaProduct {
    if (!raw || typeof raw !== 'object') {
      throw new PautaParseError('Payload de produto inválido')
    }
    const data = raw as PautaRawProductPayload
    const codigo = String(data.code ?? data.sku ?? '').trim()
    const descricao = String(data.name ?? data.title ?? '').trim()
    if (!codigo && !descricao) {
      throw new PautaParseError('Produto sem código e descrição')
    }

    return {
      codigo: codigo || `PAUTA-${hashHint(descricao)}`,
      fabricante: String(data.brand ?? data.manufacturer ?? '').trim(),
      descricao: descricao || codigo,
      preco: toNumber(data.price),
      estoque: toNumber(data.stock ?? data.qty),
      url: String(data.url ?? data.link ?? '').trim(),
      prazo: data.delivery ?? data.prazo ?? null,
      moeda: String(data.currency ?? 'BRL'),
    }
  }

  parseProducts(rawList: unknown): PautaProduct[] {
    if (!Array.isArray(rawList)) {
      throw new PautaParseError('Lista de produtos inválida')
    }
    const out: PautaProduct[] = []
    for (const item of rawList) {
      try {
        out.push(this.parseProduct(item))
      } catch {
        // Skip malformed rows — resilient for future scrapers.
      }
    }
    return out
  }

  /**
   * Reserved for HTML → structure conversion (Playwright/scraping sprint).
   * Always throws Not Implemented in Sprint 3.2.
   */
  parseHtml(_html: string): never {
    throw new PautaParseError(
      'parseHtml reservado — scraping/Playwright ainda não implementados.',
    )
  }
}

function toNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const normalized = value.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.')
    const n = Number.parseFloat(normalized)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function hashHint(value: string): string {
  let h = 0
  for (let i = 0; i < value.length; i += 1) h = (h * 31 + value.charCodeAt(i)) >>> 0
  return h.toString(36).slice(0, 6).toUpperCase()
}

export const pautaParser = new PautaParser()
